// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { Alert, Badge, Button, Skeleton, Spinner } from '@loa/ui-components';
import {
  executiveSummaryApi,
  type ExecutiveSummary,
  type RecentChange,
} from '../../api/executiveSummaryApi';

const POLL_INTERVAL_MS = 3000;

/**
 * "What changed" highlight at the top of the Summary panel — the most recent
 * material situation changes so the advisor gets an immediate read. Renders
 * nothing when there are none. See docs/specs/relationship-summary-panels-prd.md §5.1.
 */
export function RecentChangesStrip({ changes }: { changes?: RecentChange[] | null }) {
  const list = (changes ?? []).filter((c) => c && (c.label || c.detail));
  if (list.length === 0) {
    return null;
  }
  return (
    <div
      data-testid="executive-summary-recent-changes"
      className="rounded-lg p-[10px_12px]"
      style={{ background: '#F2F1FF', border: '1px solid #BDB8FF' }}
    >
      <p className="text-body-04 font-semibold text-[#5B21B6] m-0 mb-xs">What changed</p>
      <ul className="flex flex-col gap-[2px] m-0 p-0 pl-md list-disc">
        {list.map((c, i) => (
          <li key={i} data-testid="executive-summary-recent-change" className="text-[13px] text-black">
            {c.label}
            {c.detail ? <span className="text-[#595959]"> — {c.detail}</span> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** True while a (re)generation is expected — drives polling + the generating UI. */
export function isInFlight(summary: ExecutiveSummary | null): boolean {
  return !!summary && (summary.generating || summary.stale
    || summary.status === 'PENDING' || summary.status === 'GENERATING');
}

// The body is LLM-generated markdown that may also carry a tight set of inline
// HTML tags for formatting markdown can't express (<u> underline, <mark>
// highlight). This sanitize allowlist is the safety boundary: only these tags,
// and NO attributes at all (strips class/style/href/onclick/…), so even raw
// HTML from the model can't inject anything. Section headers come from `##`.
export const SUMMARY_SANITIZE_SCHEMA = {
  tagNames: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'mark', 'del', 'ins',
    'h2', 'h3', 'h4', 'ul', 'ol', 'li'],
  attributes: {},
  protocols: {},
};

const mdEl = (Tag: 'h4' | 'p' | 'strong' | 'em' | 'u' | 'mark' | 'ul' | 'ol' | 'li', className?: string) =>
  ({ children }: { children?: ReactNode }) => <Tag className={className}>{children}</Tag>;

const HEADING = 'text-[15px] font-semibold text-black mt-[14px] mb-[2px] first:mt-0';
// Inline marks + block styles for the rendered summary.
const SUMMARY_MD_COMPONENTS = {
  h2: mdEl('h4', HEADING),
  h3: mdEl('h4', HEADING),
  h4: mdEl('h4', HEADING),
  p: mdEl('p', 'text-body-03 leading-[1.5] text-[#3C3C3C] m-0'),
  strong: mdEl('strong', 'font-semibold text-black'),
  em: mdEl('em', 'italic'),
  u: mdEl('u', 'underline'),
  ins: mdEl('u', 'underline'),
  mark: mdEl('mark', 'bg-[#FFF3BF] text-inherit rounded-[3px] px-[3px]'),
  ul: mdEl('ul', 'list-none m-0 p-0 flex flex-col gap-[2px]'),
  ol: mdEl('ol', 'list-none m-0 p-0 flex flex-col gap-[2px]'),
  li: mdEl('li', 'text-body-03 leading-[1.5] text-[#3C3C3C]'),
};

interface Props {
  profileId: string;
}

/**
 * Relationship executive summary — first card on the Overview tab. Shows the
 * LLM-generated headline + 2-3 paragraphs, polling while it (re)generates and
 * offering a manual Generate / Regenerate. See docs/specs/executive-summary-prd.md.
 */
export function ExecutiveSummaryCard({ profileId }: Props) {
  const [summary, setSummary] = useState<ExecutiveSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState(false);
  const pollRef = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const fetchOnce = useCallback(async () => {
    const data = await executiveSummaryApi.get(profileId);
    setSummary(data);
    if (!isInFlight(data)) {
      stopPolling();
    }
    return data;
  }, [profileId, stopPolling]);

  const startPolling = useCallback(() => {
    if (pollRef.current !== null) {
      return;
    }
    pollRef.current = window.setInterval(() => {
      void fetchOnce().catch(() => { /* transient poll error — keep polling */ });
    }, POLL_INTERVAL_MS);
  }, [fetchOnce]);

  // Initial load.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    executiveSummaryApi.get(profileId)
      .then((data) => {
        if (cancelled) {
          return;
        }
        setSummary(data);
        if (isInFlight(data)) {
          startPolling();
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Could not load the executive summary.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [profileId, startPolling, stopPolling]);

  const onGenerate = useCallback(async () => {
    setActing(true);
    setError(null);
    try {
      const data = await executiveSummaryApi.regenerate(profileId);
      setSummary(data);
      startPolling();
    } catch {
      setError('Could not start generation. Try again.');
    } finally {
      setActing(false);
    }
  }, [profileId, startPolling]);

  const card = (children: React.ReactNode) => (
    <div
      data-testid="executive-summary-card"
      className="flex flex-col gap-[12px] py-lg px-md rounded-card bg-white shadow-[0_0_10px_0_rgba(0,0,0,0.10)] mb-lg"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-[18px] font-medium text-black font-sans m-0">Executive summary</h3>
        {summary?.stale && (
          <Badge data-testid="executive-summary-stale-badge" color="orange" size="sm">Outdated</Badge>
        )}
      </div>
      {children}
    </div>
  );

  if (loading) {
    return card(<div data-testid="executive-summary-loading" className="py-sm"><Spinner /></div>);
  }

  if (error) {
    return card(
      <div data-testid="executive-summary-error">
        <Alert variant="error">{error}</Alert>
        <div className="mt-[12px]">
          <Button size="sm" variant="outlined" onClick={onGenerate} disabled={acting}>Try again</Button>
        </div>
      </div>,
    );
  }

  if (!summary) {
    return card(
      <div data-testid="executive-summary-empty" className="flex flex-col items-start gap-[12px]">
        <p className="text-body-03 text-[#5C5C5C] m-0">No executive summary yet.</p>
        <Button size="sm" onClick={onGenerate} disabled={acting} data-testid="executive-summary-generate">
          {acting ? 'Starting…' : 'Generate'}
        </Button>
      </div>,
    );
  }

  if (summary.status === 'FAILED') {
    return card(
      <div data-testid="executive-summary-failed" className="flex flex-col gap-[12px]">
        <Alert variant="error">{summary.failureReason || 'Generation failed.'}</Alert>
        <Button size="sm" variant="outlined" onClick={onGenerate} disabled={acting} data-testid="executive-summary-regenerate">
          Try again
        </Button>
      </div>,
    );
  }

  const hasBody = !!summary.body;
  // Generating with nothing to show yet → skeleton placeholders.
  if (summary.generating && !hasBody) {
    return card(
      <div data-testid="executive-summary-generating" className="flex flex-col gap-sm">
        <Skeleton variant="line" width="60%" height="20px" />
        <Skeleton variant="paragraph" lines={3} />
      </div>,
    );
  }

  return card(
    <div data-testid="executive-summary-ready" className="flex flex-col gap-sm">
      {summary.headline && (
        <p data-testid="executive-summary-headline" className="text-body-02 font-medium text-black m-0">
          {summary.headline}
        </p>
      )}
      <RecentChangesStrip changes={summary.recentChanges?.changes} />
      <div data-testid="executive-summary-body" className="flex flex-col gap-xs">
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkBreaks]}
          // rehype-raw parses the allowed inline HTML (<u>/<mark>); rehype-sanitize
          // then strips everything outside SUMMARY_SANITIZE_SCHEMA. (Not skipHtml —
          // we deliberately allow the tight sanitized subset here.)
          rehypePlugins={[rehypeRaw, [rehypeSanitize, SUMMARY_SANITIZE_SCHEMA]]}
          components={SUMMARY_MD_COMPONENTS}
        >
          {summary.body ?? ''}
        </ReactMarkdown>
      </div>
      <div className="flex items-center gap-[12px] mt-sm">
        <Button size="sm" variant="outlined" onClick={onGenerate} disabled={acting} data-testid="executive-summary-regenerate">
          {acting ? 'Starting…' : 'Regenerate'}
        </Button>
        {summary.generatedAt && (
          <span className="text-body-04 text-[#8C8C8C]">
            Generated {new Date(summary.generatedAt).toLocaleDateString()}
          </span>
        )}
      </div>
    </div>,
  );
}

export default ExecutiveSummaryCard;
